import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  Avatar,
  LinearProgress,
  Fab,
  Tabs,
  Tab,
  Paper,
  Typography,
  Box
} from '@mui/material';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOffers } from '../services/offerService';
import brandService from '../services/brandService';
import { formatDistanceToNow } from 'date-fns';
import DashboardLayout from '../components/DashboardLayout';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [offers, setOffers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [offersData, brandsData] = await Promise.all([
        getOffers(1, 6),
        brandService.getAllBrands({ page: 1, limit: 6 })
      ]);
      setOffers(offersData.offers || []);
      setBrands(brandsData.brands || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const EventCard = ({ offer }) => {
    const isExpired = new Date(offer.expirationDate) < new Date();
    
    return (
      <Card sx={{ height: '100%', position: 'relative' }}>
        {offer.featuredImage && (
          <CardMedia
            component="img"
            height="160"
            image={offer.featuredImage}
            alt={offer.title}
            sx={{ objectFit: 'cover' }}
          />
        )}
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              {offer.title}
            </Typography>
            <Chip 
              label={isExpired ? 'Past' : 'Live'} 
              size="small"
              color={isExpired ? 'default' : 'success'}
              sx={{ ml: 1 }}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
            {offer.description?.substring(0, 100)}...
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(offer.expirationDate), { addSuffix: true })}
            </Typography>
          </Box>

          {offer.brand && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Avatar sx={{ width: 20, height: 20 }} src={offer.brand.logo}>
                {offer.brand.name.charAt(0)}
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {offer.brand.name}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <LinearProgress 
              variant="determinate" 
              value={Math.random() * 100} 
              sx={{ flex: 1, mr: 2, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary">
              {Math.floor(Math.random() * 100)}% Capacity
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const dashboardActions = (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={() => navigate('/offers/new')}
    >
      New Event
    </Button>
  );

  return (
    <DashboardLayout title="Events" actions={dashboardActions}>
      <Box sx={{ maxWidth: 'xl', mx: 'auto', px: 3, py: 2 }}>
        {/* Tabs */}
        <Box sx={{ mb: 4 }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="All" />
            <Tab label="Live" />
            <Tab label="Upcoming" />
            <Tab label="Past" />
          </Tabs>
        </Box>

        {/* Events Grid */}
        <Grid container spacing={3}>
          {loading ? (
            Array.from(new Array(6)).map((_, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography color="text.secondary">Loading...</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            offers.map((offer) => (
              <Grid item xs={12} sm={6} lg={4} key={offer._id}>
                <EventCard offer={offer} />
              </Grid>
            ))
          )}
        </Grid>

        {/* Empty state */}
        {!loading && offers.length === 0 && (
          <Paper sx={{ p: 8, textAlign: 'center', mt: 4 }}>
            <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No events found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create your first event to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/offers/new')}
            >
              Create Event
            </Button>
          </Paper>
        )}
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', sm: 'none' }
        }}
        onClick={() => navigate('/offers/new')}
      >
        <AddIcon />
      </Fab>
    </DashboardLayout>
  );
};

export default Dashboard; 